import React, { useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Send, Eye, CheckCircle2, Ban, Copy, Sparkles, Plus, RefreshCcw, FileCheck2, ShieldCheck, ClipboardList } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);
const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  draft:     "bg-slate-100 text-slate-700 border-slate-300",
  pending:   "bg-amber-100 text-amber-800 border-amber-300",
  signed:    "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-red-100 text-red-800 border-red-300",
};

const DEDUCTION_OPTIONS = [
  "Fuel", "Insurance", "Vehicle Hire", "Management Fee",
  "Parking", "Tolls", "Repairs", "Tyres", "Damage Charges", "Other",
];

const AdminContracts = () => {
  const { data: contracts = [], mutate } = useSWR("/contracts", fetcher);
  const { data: templates = [] } = useSWR("/contracts/templates", fetcher);
  const { data: drivers = [] } = useSWR("/drivers?limit=500", fetcher);
  const { data: currentUser } = useSWR("/auth/me", fetcher);
  const isOwner = currentUser?.role === "platform_owner";
  const { data: companies = [] } = useSWR(isOwner ? "/companies" : null, fetcher);
  const [tenantId, setTenantId] = useState("");
  const scopedCompanyId = isOwner ? tenantId : (currentUser?.company_id || "");
  const approvalsUrl = scopedCompanyId
    ? `/contracts/template-approvals?company_id=${scopedCompanyId}`
    : "/contracts/template-approvals";
  const { data: approvals, mutate: reloadApprovals } = useSWR(approvalsUrl, fetcher);
  const [createOpen, setCreateOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);

  const toggleTemplateApproval = async (code, next) => {
    if (isOwner && !tenantId) {
      toast.error("Pick a tenant first — Platform Owner must scope approvals to a company.");
      return;
    }
    try {
      const body = { approved: next };
      if (isOwner && tenantId) body.company_id = tenantId;
      await api.post(`/contracts/template-approvals/${code}`, body);
      toast.success(next
        ? "Marked as solicitor-approved — DRAFT watermark removed from future PDFs"
        : "Approval revoked — DRAFT watermark will reappear on future PDFs");
      reloadApprovals();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/sign/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Signing link copied — share it with the driver");
  };

  const send = async (id) => {
    try {
      const { data } = await api.post(`/contracts/${id}/send`);
      const url = `${window.location.origin}${data.signing_url}`;
      navigator.clipboard.writeText(url);
      toast.success("Signing link copied to clipboard");
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/contracts/${id}/approve`);
      toast.success("Contract approved — DRAFT watermark removed");
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this contract?")) return;
    try { await api.delete(`/contracts/${id}`); toast.success("Cancelled"); mutate(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/contracts/${id}/duplicate`);
      toast.success(`Duplicated as ${data.contract_ref}`);
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Duplicate failed"); }
  };

  const renew = async (id) => {
    if (!window.confirm("Create a renewal for this contract (starts today, expires in 12 months)?")) return;
    try {
      const { data } = await api.post(`/contracts/${id}/renew`, {});
      toast.success(`Renewal created ${data.contract_ref}`);
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Renew failed"); }
  };

  const downloadCert = (id, ref) => window.open(`${API}/api/contracts/${id}/certificate`, "_blank", "noopener");

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Contracts</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Contract Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Generate PDFs from templates, send secure signing links, and keep an immutable audit trail.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-brand-red hover:bg-brand-red-hover"
                data-testid="ct-create-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New contract
        </Button>
      </div>

      <Card className="p-4 rounded-2xl bg-amber-50 border-amber-200 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-amber-600 mt-0.5" />
        <div className="text-xs text-amber-800 flex-1">
          <strong>Starter templates — solicitor review required.</strong> Templates are drafted for administrative
          convenience only. Every generated PDF carries a diagonal &quot;DRAFT&quot; watermark until you click
          <em> Approve </em> on a specific contract. Only approve after your solicitor has reviewed the wording.
        </div>
        <Button size="sm" variant="outline" onClick={() => setApprovalsOpen(true)}
                data-testid="ct-approvals-btn"
                className="border-amber-300 text-amber-800 hover:bg-amber-100">
          <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Template approvals
        </Button>
      </Card>

      <Dialog open={approvalsOpen} onOpenChange={setApprovalsOpen}>
        <DialogContent className="max-w-lg" data-testid="ct-approvals-dialog">
          <DialogHeader>
            <DialogTitle>Solicitor-approved templates</DialogTitle>
            <DialogDescription>
              Flip a template on once your solicitor has reviewed the wording — future
              PDFs generated from that template will render <b>without</b> the
              &quot;DRAFT — Solicitor Review Required&quot; watermark. Existing signed
              PDFs on disk are never re-rendered.
            </DialogDescription>
          </DialogHeader>
          {isOwner && (
            <div className="mb-1">
              <Label className="text-xs uppercase tracking-wider">Tenant</Label>
              <Select value={tenantId || ""} onValueChange={setTenantId}>
                <SelectTrigger data-testid="ct-approvals-tenant"
                                className="mt-1.5">
                  <SelectValue placeholder="— Pick a tenant to manage —" />
                </SelectTrigger>
                <SelectContent>
                  {(companies || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name || c.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!tenantId && (
                <p className="mt-1.5 text-[11px] text-brand-text-muted">
                  Approvals are per-tenant. Pick a company above to enable the toggles.
                </p>
              )}
            </div>
          )}
          <ul className={`mt-2 divide-y divide-brand-border max-h-[420px] overflow-y-auto rounded-lg border border-brand-border ${
            isOwner && !tenantId ? "opacity-50 pointer-events-none" : ""
          }`}>
            {templates.map((t) => {
              const approvedRec = approvals?.approved?.[t.code];
              const on = !!approvedRec;
              return (
                <li key={t.code} className="flex items-center justify-between px-3 py-2.5">
                  <div className="min-w-0 pr-3">
                    <div className="text-xs font-medium truncate">{t.title}</div>
                    <div className="text-[10px] text-brand-text-muted truncate">
                      {on
                        ? `Approved by ${approvedRec.approved_by_name || "—"} on ${approvedRec.approved_at?.slice(0, 10)}`
                        : t.description}
                    </div>
                  </div>
                  <button type="button"
                          onClick={() => toggleTemplateApproval(t.code, !on)}
                          data-testid={`ct-approve-toggle-${t.code}`}
                          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                            on ? "bg-emerald-500" : "bg-slate-300"
                          }`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      on ? "translate-x-5" : "translate-x-0.5"
                    }`} />
                  </button>
                </li>
              );
            })}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-0 rounded-2xl border-brand-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Ref</th>
              <th className="text-left px-4 py-3">Template</th>
              <th className="text-left px-4 py-3">Driver</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contracts.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400 text-sm">
                No contracts yet — click &quot;New contract&quot; to generate the first one.
              </td></tr>
            )}
            {contracts.map((c) => {
              const drv = drivers.find((d) => d.id === c.driver_id);
              return (
                <tr key={c.id} className="border-t border-brand-border hover:bg-slate-50/60" data-testid={`ct-row-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{c.contract_ref}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.title}</div>
                    <div className="text-[10px] text-slate-400">v{c.template_version}</div>
                  </td>
                  <td className="px-4 py-3">{drv?.full_name || c.driver_id.slice(0, 8)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${STATUS_STYLE[c.status] || ""} border`}>{c.status}</Badge>
                    {!c.approved && c.status !== "cancelled" && (
                      <Badge className="ml-1.5 bg-amber-100 text-amber-800 border border-amber-300">DRAFT</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {c.created_at?.slice(0, 10)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`ct-pdf-${c.id}`}
                              onClick={() => window.open(`${API}/api/contracts/${c.id}/pdf`, "_blank", "noopener")}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      {!c.approved && c.status !== "signed" && c.status !== "cancelled" && (
                        <Button size="sm" variant="ghost" title="Approve template (removes DRAFT watermark)"
                                onClick={() => approve(c.id)} data-testid={`ct-approve-${c.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        </Button>
                      )}
                      {c.status !== "signed" && c.status !== "cancelled" && (
                        <Button size="sm" variant="ghost" title="Send / copy signing link"
                                onClick={() => send(c.id)} data-testid={`ct-send-${c.id}`}>
                          <Send className="w-3.5 h-3.5 text-brand-navy" />
                        </Button>
                      )}
                      {c.status !== "signed" && c.status !== "cancelled" && (
                        <Button size="sm" variant="ghost" title="Cancel contract"
                                onClick={() => cancel(c.id)} data-testid={`ct-cancel-${c.id}`}>
                          <Ban className="w-3.5 h-3.5 text-red-600" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" title="Duplicate as new draft"
                              onClick={() => duplicate(c.id)} data-testid={`ct-dup-${c.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      {c.status === "signed" && (
                        <>
                          <Button size="sm" variant="ghost" title="Signature certificate"
                                  onClick={() => downloadCert(c.id)} data-testid={`ct-cert-${c.id}`}>
                            <FileCheck2 className="w-3.5 h-3.5 text-emerald-600" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Renew contract"
                                  onClick={() => renew(c.id)} data-testid={`ct-renew-${c.id}`}>
                            <RefreshCcw className="w-3.5 h-3.5 text-brand-navy" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <CreateContractDialog open={createOpen} onOpenChange={setCreateOpen}
                              drivers={drivers} templates={templates}
                              onCreated={() => { setCreateOpen(false); mutate(); }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------

const CreateContractDialog = ({ open, onOpenChange, drivers, templates, onCreated }) => {
  const [driverId, setDriverId] = useState("");
  const [templateCode, setTemplateCode] = useState("master");
  const [dailyRate, setDailyRate] = useState("");
  const [stopRateOver, setStopRateOver] = useState("");
  const [stopThreshold, setStopThreshold] = useState("");
  const [managementFee, setManagementFee] = useState("");
  const [paymentModel, setPaymentModel] = useState("Daily Rate");
  const [expenseModel, setExpenseModel] = useState("Company Pays Then Deducts");
  const [paymentFrequency, setPaymentFrequency] = useState("Weekly (4-week DPD calendar)");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [deductions, setDeductions] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleDeduction = (d) =>
    setDeductions((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]);

  const submit = async () => {
    if (!driverId) return toast.error("Select a driver");
    setBusy(true);
    try {
      await api.post("/contracts", {
        driver_id: driverId,
        template_code: templateCode,
        variables: {
          StartDate: startDate,
          DailyRate: dailyRate || undefined,
          StopRateOver: stopRateOver || undefined,
          StopThreshold: stopThreshold || undefined,
          ManagementFee: managementFee || undefined,
          PaymentModel: paymentModel,
          ExpenseModel: expenseModel,
          PaymentFrequency: paymentFrequency,
          DeductionsList: deductions,
        },
        notes: notes || undefined,
      });
      toast.success("Contract generated");
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate contract</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Driver *</Label>
              <Select value={driverId} onValueChange={setDriverId}>
                <SelectTrigger data-testid="ct-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.full_name} ({d.driver_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Template *</Label>
              <Select value={templateCode} onValueChange={setTemplateCode}>
                <SelectTrigger data-testid="ct-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div><Label className="text-xs">Payment model</Label>
              <Select value={paymentModel} onValueChange={setPaymentModel}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Daily Rate", "Stop Rate", "Owner Driver", "Revenue Share", "Custom"].map((x) =>
                    <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Daily rate (£)</Label>
              <Input value={dailyRate} onChange={(e) => setDailyRate(e.target.value)} placeholder="140.00" />
            </div>
            <div><Label className="text-xs">Additional stop rate (£)</Label>
              <Input value={stopRateOver} onChange={(e) => setStopRateOver(e.target.value)} placeholder="0.65" />
            </div>
            <div><Label className="text-xs">Stop threshold</Label>
              <Input value={stopThreshold} onChange={(e) => setStopThreshold(e.target.value)} placeholder="180" />
            </div>
            <div><Label className="text-xs">Management fee</Label>
              <Input value={managementFee} onChange={(e) => setManagementFee(e.target.value)} placeholder="£20 / week" />
            </div>
            <div className="col-span-2"><Label className="text-xs">Expense model</Label>
              <Select value={expenseModel} onValueChange={setExpenseModel}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Company Pays Everything", "Company Pays Then Deducts", "Driver Pays Everything"].map((x) =>
                    <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Payment frequency</Label>
              <Input value={paymentFrequency} onChange={(e) => setPaymentFrequency(e.target.value)} />
            </div>
          </div>

          <div>
            <Label className="text-xs mb-2 block">Applicable deductions (appear inside the contract)</Label>
            <div className="grid grid-cols-3 gap-2">
              {DEDUCTION_OPTIONS.map((d) => (
                <label key={d} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-slate-50">
                  <input type="checkbox" checked={deductions.includes(d)} onChange={() => toggleDeduction(d)}
                          data-testid={`ct-ded-${d}`} />
                  {d}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Admin notes (internal only)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-brand-red hover:bg-brand-red-hover"
                  data-testid="ct-submit">
            {busy ? "Generating…" : "Generate PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminContracts;
